import { useId, useState } from 'react'
import { ChevronDown, Lightbulb } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { itemLinesOf, kindLabelOf, notAppliedNote, sourceLabelOf, sourceStateNote, splitViewItems } from './basisUtils'
import type { GenerationBasisView, GenerationBasisViewItem } from './types'

interface GenerationBasisSummaryProps {
    /** What was supplied to the generation, as stored; nothing is shown without it */
    basis: GenerationBasisView | null | undefined
    /** Opens the list in a popover, for tight places such as a table row */
    popover?: boolean
    className?: string
}

const SUPPLIED_TEXT = 'Based on'

const ViewItemList = ({ items, label }: { items: GenerationBasisViewItem[]; label: string }) => (
    <ul aria-label={label} className="tw:flex tw:list-none tw:flex-col tw:gap-1 tw:!p-0 tw:!m-0">
        {items.map((item) => {
            const lines = itemLinesOf(item)
            if (lines.length === 0 && !item.redacted) return null
            return (
                <li key={item.key} className="tw:flex tw:flex-col tw:text-xs">
                    <span className="tw:text-gray-200">
                        {sourceLabelOf(item.source)}: {kindLabelOf(item.kind)}
                    </span>
                    {lines.length > 0 ? (
                        lines.map((line, index) => (
                            <span key={index} className="tw:break-words">
                                {line}
                            </span>
                        ))
                    ) : (
                        <span className="tw:text-gray-200">{item.redacted ? 'Removed from the record' : ''}</span>
                    )}
                </li>
            )
        })}
    </ul>
)

/** The notes that explain why nothing was applied, one per source. */
const notesOf = (basis: GenerationBasisView): string[] => {
    const notes = new Set<string>()
    basis.sources.forEach((source) => {
        const text =
            notAppliedNote(source.notApplied, source.source) ??
            (basis.items.length === 0 ? sourceStateNote(source.state, source.reason, source.source) : null)
        if (text) notes.add(text)
    })
    return Array.from(notes)
}

const headingOf = (basis: GenerationBasisView): string => {
    const count = basis.items.length
    if (count === 0) return 'No insight used'
    const sources = Array.from(new Set(basis.items.map((item) => sourceLabelOf(item.source).toLowerCase())))
    return `${SUPPLIED_TEXT} ${count} ${count === 1 ? 'item' : 'items'} from ${sources.join(' and ')}`
}

const Details = ({ basis }: { basis: GenerationBasisView }) => {
    const notes = notesOf(basis)
    const { cited, others } = splitViewItems(basis)
    return (
        <div className="tw:flex tw:flex-col tw:gap-2" data-testid="generation-basis-details">
            {notes.map((note) => (
                <p key={note} className="tw:m-0 tw:text-xs tw:text-gray-200">
                    {note}
                </p>
            ))}
            {cited.length > 0 && (
                <div className="tw:flex tw:flex-col tw:gap-1">
                    <span className="tw:text-xs tw:font-semibold">The model says these shaped it</span>
                    <ViewItemList items={cited} label="Items the model says shaped it" />
                </div>
            )}
            {others.length > 0 && (
                <div className="tw:flex tw:flex-col tw:gap-1">
                    {cited.length > 0 && <span className="tw:text-xs tw:font-semibold">Also supplied</span>}
                    <ViewItemList items={others} label="Items supplied" />
                </div>
            )}
        </div>
    )
}

/**
 * "Based on" for a stored generation: collapsed to one line, it lists what was supplied to it (from the stored
 * record, so it reads the same later). Items the model itself says shaped the result are named as its own claim.
 */
const GenerationBasisSummary = ({ basis, popover = false, className }: GenerationBasisSummaryProps) => {
    const [open, setOpen] = useState(false)
    const detailsId = useId()

    if (!basis) return null

    const heading = headingOf(basis)

    if (popover) {
        return (
            <Popover>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        aria-label={heading}
                        title={heading}
                        data-testid="generation-basis-toggle"
                        className={cn(
                            'tw:inline-flex tw:shrink-0 tw:items-center tw:rounded tw:border-0 tw:bg-transparent tw:p-0.5 tw:text-gray-200 tw:hover:text-white',
                            className,
                        )}
                    >
                        <Lightbulb size={16} />
                    </button>
                </PopoverTrigger>
                <PopoverContent className="tw:w-80">
                    <div className="tw:flex tw:flex-col tw:gap-2">
                        <span className="tw:text-sm tw:font-semibold">{heading}</span>
                        <Details basis={basis} />
                    </div>
                </PopoverContent>
            </Popover>
        )
    }

    return (
        <div className={cn('tw:flex tw:flex-col tw:gap-1', className)} data-testid="generation-basis-summary">
            <button
                type="button"
                aria-expanded={open}
                aria-controls={detailsId}
                data-testid="generation-basis-toggle"
                onClick={() => setOpen((value) => !value)}
                className="tw:inline-flex tw:w-fit tw:items-center tw:gap-1 tw:border-0 tw:bg-transparent tw:p-0 tw:text-xs tw:text-gray-200 tw:hover:text-white"
            >
                <ChevronDown size={14} className={cn('tw:transition-transform', { 'tw:rotate-180': open })} />
                {heading}
            </button>
            <div id={detailsId} hidden={!open}>
                {open && <Details basis={basis} />}
            </div>
        </div>
    )
}

export default GenerationBasisSummary
